import React from 'react';
import { useProjects } from '../hooks/useProjects';
import { ProjectStats } from '../components/ProjectStats';
import { ProjectPipeline } from '../components/ProjectPipeline';
import { ProjectGridView } from '../components/ProjectGridView';
import FilterSection from '@/components/ui/FilterSection';
import ActivityAdvancedFilters from '@/components/ui/ActivityAdvancedFilters';
import Toast from '@/components/ui/Toast';
import Pagination from '@/components/ui/Pagination';
import DataTable from '@/components/ui/DataTable';
import { AddProjectModal } from '../components/AddProjectModal';
import { Eye, Pencil, User, ClipboardList } from 'lucide-react';
import { ViewProjectModal } from '../components/ViewProjectModal';
import { useNavigate } from 'react-router-dom';
import { getTowerProjectColumns } from '../components/towerProjectColumns';
import { PROJECT_PIPELINE_STAGES } from '../constants/projectConstants';

const DISPATCHED_STATUSES = [
  "Dispatched",
  "Project started",
  "Civil Complete",
  "Electrical Complete",
  "Tower Complete",
  "Balance Update",
  "Documents",
  "Meter Connection",
  "Subsidy",
  "Completed",
];

export const ProjectsScreen = () => {
  const navigate = useNavigate();

  const {
    projects,
    fetchProjects,
    stats,
    isLoading,
    isModalOpen,
    setIsModalOpen,
    showDetails,
    viewMode,
    setViewMode,
    handleSearch,
    statusFilter,
    handleStatusFilter,
    customerName,
    setCustomerName,
    customerList,
    statusList,
    dateRange,
    datePreset,
    handleStartDateChange,
    handleEndDateChange,
    handleDatePreset,
    handleProjectSubmit,
    toast,
    setToast,
    page,
    setPage,
    totalPages,
    totalItems,
    editingProject,
    setEditingProject,
    assigningId,
    setAssigningId,
    handleAssignManager,
    user,
    canAddProject,
    quickViewId,
    setQuickViewId,
    managers,
    searchQuery,
    handleExportProjects
  } = useProjects();

  const handleEditProject = (project) => {
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const columnsWithActions = getTowerProjectColumns({
    assigningId,
    setAssigningId,
    managers,
    handleAssignManager,
    setQuickViewId,
    navigate,
    handleEditProject,
    userRole: user?.role
  });

  return (
    <div className="flex flex-col gap-4 w-full min-w-0 max-w-full animate-in fade-in duration-700 relative">
      <div className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm">
        <div className="pl-8 z-20">
          <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
            Manage Projects
          </h1>
          <p className="text-sm font-body text-slate-400 mt-2 font-medium">Verve Kitchens Project Management</p>
        </div>
        <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
          <img
            src="/kitchens/1.jpg"
            alt="Manage Projects"
            className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
          />
        </div>
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <ProjectStats showDetails={showDetails} stats={stats} />

      {/* <ProjectPipeline stats={stats.pipelineStats} /> */}
        <FilterSection
          search={searchQuery}
          setSearch={handleSearch}
          filters={{
            status: statusFilter === 'All statuses' ? 'all' : statusFilter,
            fromDate: dateRange.from,
            toDate: dateRange.to,
            date: datePreset,
            customerName: customerName
          }}
          setFilters={(key, val) => {
            // Object form: { fromDate, toDate, date, status, customerName }
            if (typeof key === 'object' && key !== null) {
              const obj = key;
              if (obj.status !== undefined) handleStatusFilter(obj.status === 'all' ? 'All statuses' : obj.status);
              if (obj.customerName !== undefined) { setCustomerName(obj.customerName); setPage(1); }

              if (obj.date !== undefined && obj.date !== 'all') {
                handleDatePreset(obj.date);
              } else if (obj.date === 'all' && (obj.fromDate === '' || obj.fromDate === undefined) && (obj.toDate === '' || obj.toDate === undefined)) {
                handleDatePreset('all');
              } else {
                if (obj.fromDate !== undefined) handleStartDateChange(obj.fromDate);
                if (obj.toDate !== undefined) handleEndDateChange(obj.toDate);
              }
              
              return;
            }
            // String key form
            if (key === 'status') handleStatusFilter(val === 'all' ? 'All statuses' : val);
            if (key === 'fromDate') handleStartDateChange(val);
            if (key === 'toDate') handleEndDateChange(val);
            if (key === 'date') handleDatePreset(val);
            if (key === 'customerName') { setCustomerName(val); setPage(1); }
          }}
          // viewStyle={viewMode}
          // setViewStyle={setViewMode}
          addLabel="Add Project"
          onAddClick={canAddProject ? () => setIsModalOpen(true) : undefined}
          onExport={handleExportProjects}
          statusPlaceholder="All statuses"
          statusOptions={[
            ...Array.from(new Set([...PROJECT_PIPELINE_STAGES, ...(statusList || [])]))
              .filter(Boolean)
              .map(s => ({ label: s, value: s }))
          ]}
          customerOptions={customerList}
        />

        {/* Kanban view disabled per request
        {viewMode === 'grid' ? (
          <div className="p-3 sm:p-4 bg-slate-50/30 min-w-0">
            <ProjectGridView
              data={projects}
              isLoading={isLoading}
              onEditProject={handleEditProject}
              onQuickView={(id) => setQuickViewId(id)}
              userRole={user?.role}
            />
          </div>
        ) : (
        */}
          <div className="min-w-0">
            <DataTable
              columns={columnsWithActions}
              data={projects}
              isLoading={isLoading}
              onRowClick={(row) => {
                if (user?.role === 'installation_team') {
                  const status = String(row.status || '').toLowerCase();
                  const allowed = DISPATCHED_STATUSES.map(s => s.toLowerCase());
                  if (!allowed.includes(status)) return;
                }
                navigate(`/projects/${row._id}`);
              }}
              getRowClassName={(index, row) => {
                let className = 'cursor-pointer ';
                if (user?.role === 'installation_team') {
                  const status = String(row.status || '').toLowerCase();
                  const allowed = DISPATCHED_STATUSES.map(s => s.toLowerCase());
                  if (!allowed.includes(status)) {
                    // For field officers, restricted projects should still look like they can be clicked 
                    // (user requested pointer) but we maintain the logic that they don't navigate.
                    return className;
                  }
                }
                return className;
              }}
            />
          </div>

        <div className="border-t border-slate-50 bg-white">
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
      </div>

      <AddProjectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProject(null);
        }}
        onSubmit={handleProjectSubmit}
        isLoading={isLoading}
        initialData={editingProject}
        // Polinaidu: 21-04-2026 (20:57) - Pass current user for auto-assignment
        user={user}
      />

      {quickViewId && (
        <ViewProjectModal
          projectId={quickViewId}
          onClose={() => setQuickViewId(null)}
          onProjectUpdated={fetchProjects}
        />
      )}
    </div>
  );
};
